import React from "react";
import { Zap, Leaf, Settings, Sparkles, CheckCircle, Layers } from "lucide-react";
import { Card, CardContent } from "./ui/card";

const CoatingsSection = () => {
  const benefits = [
    {
      icon: Zap,
      title: "Instant Curing",
      description: "UV/LED technology enables immediate curing, dramatically reducing production time and increasing throughput."
    },
    {
      icon: Leaf,
      title: "Energy Efficient",
      description: "Significant energy savings compared to traditional thermal curing methods, reducing operational costs."
    },
    {
      icon: Sparkles,
      title: "Superior Finish",
      description: "Exceptional gloss, hardness, and scratch resistance delivering premium quality results."
    },
    {
      icon: Leaf,
      title: "Minimal VOC",
      description: "Eco-friendly formulations with minimal volatile organic compound emissions for safer workplaces."
    },
    {
      icon: Settings,
      title: "Custom Formulations",
      description: "Tailored solutions designed to meet your specific industrial requirements and printing processes."
    },
    {
      icon: Layers,
      title: "Multi-Process Compatible",
      description: "Optimized for offset, screen, gravure, and flexo printing applications."
    }
  ];

  const processes = [
    { name: "Offset Printing", color: "bg-red-100 text-[#8B1538]" },
    { name: "Screen Printing", color: "bg-red-100 text-[#8B1538]" },
    { name: "Gravure Printing", color: "bg-red-100 text-[#8B1538]" },
    { name: "Flexo Printing", color: "bg-red-100 text-[#8B1538]" }
  ];

  return (
    <section id="coatings" className="section-padding relative overflow-hidden reveal">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1693031630177-b897fb9d7154?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxVViUyMHByaW50aW5nfGVufDB8fHx8MTc2MTU5MjQwMnww&ixlib=rb-4.1.0&q=85"
          alt="UV Coating Technology"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/95 via-white/95 to-red-50/95"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#8B1538] font-semibold text-sm uppercase tracking-wider">Core Expertise</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            UV & LED Curable Coating Solutions
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Advanced coating technologies engineered for superior performance, sustainability, and versatility across all major printing processes.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="bg-red-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#8B1538] transition-colors">
                  <benefit.icon className="h-7 w-7 text-[#8B1538] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compatible Printing Processes */}
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Compatible Printing Processes</h3>
            <p className="text-lg text-gray-600">Our UV & LED curable coatings are optimized for:</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processes.map((process, index) => (
              <div key={index} className={`${process.color} rounded-xl p-6 text-center font-bold text-lg shadow-md hover:shadow-lg transition-shadow`}>
                <CheckCircle className="h-8 w-8 mx-auto mb-3" />
                {process.name}
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-red-50 to-white rounded-xl p-8 border border-red-100">
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="bg-[#8B1538] p-4 rounded-full">
                <Settings className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Custom Coating Development</h4>
                <p className="text-gray-600 text-lg">
                  Need a specialized formulation? Our R&D team develops custom UV and LED curable coatings tailored to your exact specifications, printing process, and substrate requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoatingsSection;
